import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText, X, ZoomIn, ZoomOut, Download, ChevronLeft, ChevronRight,
  Loader2, ExternalLink, Maximize2, Minimize2, File, Image
} from 'lucide-react';
import axios from 'axios';
import { auth } from '../../utils/auth';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * DOCUMENT VIEWER PANEL
 * 
 * Split view panel for viewing documents alongside AI chat.
 * Supports:
 * - PDF viewing (embedded)
 * - Image viewing
 * - Text file preview
 * - Document metadata display
 */
const DocumentViewerPanel = ({ 
  document, 
  onClose, 
  onDocumentChange,
  availableDocuments = [],
  caseId 
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [documentUrl, setDocumentUrl] = useState(null);
  const [zoom, setZoom] = useState(100);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [textContent, setTextContent] = useState(null);

  // Fetch document content/URL
  const fetchDocument = useCallback(async () => {
    if (!document?.id) return;
    
    setLoading(true);
    setError(null);
    setTextContent(null);
    
    try {
      const token = auth.getToken();
      const headers = { Authorization: `Bearer ${token}` };
      
      // Get document details
      const docRes = await axios.get(
        `${API}/documents/${document.id}`,
        { headers }
      );
      
      const docData = docRes.data;
      const fileType = docData.file_type?.toLowerCase() || '';
      const filename = docData.filename?.toLowerCase() || '';
      
      // For Word documents (.docx), use the preview endpoint which converts to HTML
      if (filename.endsWith('.docx') || fileType.includes('wordprocessingml')) {
        try {
          const previewRes = await axios.get(
            `${API}/documents/${document.id}/preview`,
            { headers, responseType: 'text' }
          );
          // Create a blob from the HTML and display in iframe
          const blob = new Blob([previewRes.data], { type: 'text/html' });
          const url = URL.createObjectURL(blob);
          setDocumentUrl(url);
        } catch (err) {
          console.error('Preview failed, falling back to metadata:', err);
          setError('Forhåndsvisning ikke tilgjengelig. Last ned for å åpne.');
        }
        
      } else if (fileType.includes('pdf') || filename.endsWith('.pdf')) {
        const downloadRes = await axios.get(
          `${API}/documents/${document.id}/download`,
          { headers, responseType: 'blob' }
        );
        const url = URL.createObjectURL(downloadRes.data);
        setDocumentUrl(url);
        
      } else if (fileType.includes('image') || /\.(jpg|jpeg|png|gif|webp)$/i.test(filename)) {
        const downloadRes = await axios.get(
          `${API}/documents/${document.id}/download`,
          { headers, responseType: 'blob' }
        );
        const url = URL.createObjectURL(downloadRes.data);
        setDocumentUrl(url);
        
      } else if (/\.(txt|md|json|csv|xml|html)$/i.test(filename) || fileType.includes('text')) {
        // For text files, fetch and display content
        const downloadRes = await axios.get(
          `${API}/documents/${document.id}/download`,
          { headers, responseType: 'text' }
        );
        setTextContent(downloadRes.data);
        
      } else {
        // For other files, show metadata only
        setError('Forhåndsvisning ikke støttet for denne filtypen. Last ned for å åpne.');
      }
      
    } catch (err) {
      console.error('Error fetching document:', err);
      setError('Kunne ikke laste dokumentet');
    } finally {
      setLoading(false);
    }
  }, [document?.id]);

  useEffect(() => {
    fetchDocument();
    
    // Cleanup blob URLs on unmount
    return () => {
      if (documentUrl) {
        URL.revokeObjectURL(documentUrl);
      }
    };
  }, [fetchDocument]);

  // Handle zoom
  const handleZoomIn = () => setZoom(prev => Math.min(prev + 25, 200));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 25, 50));

  // Download document
  const handleDownload = async () => {
    try {
      const token = auth.getToken();
      const response = await axios.get(
        `${API}/documents/${document.id}/download`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = URL.createObjectURL(response.data);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = document.filename || 'document';
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
    }
  };

  // Navigate between documents
  const currentIndex = availableDocuments.findIndex(d => d.id === document?.id);
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < availableDocuments.length - 1;

  const goToPrev = () => {
    if (hasPrev) {
      onDocumentChange(availableDocuments[currentIndex - 1]);
    }
  };

  const goToNext = () => {
    if (hasNext) {
      onDocumentChange(availableDocuments[currentIndex + 1]);
    }
  };

  // Determine file type for display
  const getFileIcon = () => {
    const filename = document?.filename?.toLowerCase() || '';
    if (filename.endsWith('.pdf')) return <FileText className="w-5 h-5 text-red-500" />;
    if (/\.(jpg|jpeg|png|gif|webp)$/i.test(filename)) return <Image className="w-5 h-5 text-blue-500" />;
    if (/\.(doc|docx)$/i.test(filename)) return <FileText className="w-5 h-5 text-blue-600" />;
    return <File className="w-5 h-5 text-gray-500" />;
  };

  const isPdf = document?.filename?.toLowerCase().endsWith('.pdf') || document?.file_type?.includes('pdf');
  const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(document?.filename || '');
  const isWord = /\.(doc|docx)$/i.test(document?.filename || '') || document?.file_type?.includes('wordprocessingml');

  return (
    <div className={cn(
      "flex flex-col bg-white border-l border-gray-200 h-full",
      isFullscreen ? "fixed inset-0 z-50" : "w-full"
    )}>
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center gap-2 min-w-0">
          {getFileIcon()}
          <div className="min-w-0">
            <h3 className="text-sm font-medium text-gray-900 truncate">
              {document?.filename || 'Dokument'}
            </h3>
            {availableDocuments.length > 1 && (
              <p className="text-[10px] text-gray-500">
                {currentIndex + 1} av {availableDocuments.length}
              </p>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-1">
          {/* Navigation */}
          {availableDocuments.length > 1 && (
            <>
              <Button
                size="sm"
                variant="ghost"
                onClick={goToPrev}
                disabled={!hasPrev}
                className="h-7 w-7 p-0"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={goToNext}
                disabled={!hasNext}
                className="h-7 w-7 p-0"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </>
          )}
          
          {/* Zoom controls */}
          {(isPdf || isImage) && (
            <>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleZoomOut}
                className="h-7 w-7 p-0"
                title="Zoom ut"
              >
                <ZoomOut className="w-4 h-4" />
              </Button>
              <span className="text-xs text-gray-500 min-w-[40px] text-center">
                {zoom}%
              </span>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleZoomIn}
                className="h-7 w-7 p-0"
                title="Zoom inn"
              >
                <ZoomIn className="w-4 h-4" />
              </Button>
            </>
          )}
          
          {/* Fullscreen toggle */}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setIsFullscreen(!isFullscreen)}
            className="h-7 w-7 p-0"
            title={isFullscreen ? "Minimer" : "Fullskjerm"}
          >
            {isFullscreen ? (
              <Minimize2 className="w-4 h-4" />
            ) : (
              <Maximize2 className="w-4 h-4" />
            )}
          </Button>
          
          {/* Download */}
          <Button
            size="sm"
            variant="ghost"
            onClick={handleDownload}
            className="h-7 w-7 p-0"
            title="Last ned"
          >
            <Download className="w-4 h-4" />
          </Button>
          
          {/* Close */}
          <Button
            size="sm"
            variant="ghost"
            onClick={onClose}
            className="h-7 w-7 p-0"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>
      
      {/* Content area */}
      <div className="flex-1 overflow-auto bg-gray-100">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-500 p-6">
            <File className="w-12 h-12 mb-3 text-gray-300" />
            <p className="text-sm text-center">{error}</p>
            <Button
              size="sm"
              onClick={handleDownload}
              className="mt-4"
            >
              <Download className="w-4 h-4 mr-2" />
              Last ned
            </Button>
          </div>
        ) : isPdf && documentUrl ? (
          <div className="h-full w-full" style={{ transform: `scale(${zoom / 100})`, transformOrigin: 'top left' }}>
            <iframe
              src={documentUrl}
              className="w-full h-full border-0"
              style={{ 
                width: `${100 * (100 / zoom)}%`, 
                height: `${100 * (100 / zoom)}%` 
              }}
              title={document?.filename}
            />
          </div>
        ) : isWord && documentUrl ? (
          <div className="h-full w-full bg-white">
            <iframe
              src={documentUrl}
              className="w-full h-full border-0"
              title={document?.filename}
              sandbox="allow-same-origin"
            />
          </div>
        ) : isImage && documentUrl ? (
          <div className="flex items-center justify-center h-full p-4">
            <img
              src={documentUrl}
              alt={document?.filename}
              className="max-w-full max-h-full object-contain"
              style={{ transform: `scale(${zoom / 100})` }}
            />
          </div>
        ) : textContent ? (
          <div className="p-4">
            <pre className="text-sm text-gray-700 whitespace-pre-wrap font-mono bg-white p-4 rounded-lg border border-gray-200">
              {textContent}
            </pre>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-gray-500">
            <File className="w-12 h-12 mb-3 text-gray-300" />
            <p className="text-sm">Velg et dokument for å vise det her</p>
          </div>
        )}
      </div>
      
      {/* Document info footer */}
      {document && (
        <div className="px-3 py-2 border-t border-gray-200 bg-gray-50 text-xs text-gray-500">
          <div className="flex items-center justify-between">
            <span>{document.file_type || 'Ukjent type'}</span>
            {document.ai_summary && (
              <span className="text-blue-600">AI-analysert</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentViewerPanel;
